<script>
  import { format } from 'svelte-i18n';

  import { getNotificationsContext } from '@client/components/notifications';

  import { BizCardsRoutingService } from '@client/services/bizcards-routing';

  import { PriorityChangeForm } from '../index.js';

  /**
   * Controls whether the next button in the workflow step is disabled.
   * @type {boolean}
   * @default true
   */
  export let nextDisabled = true;

  /**
   * Callback function invoked when the workflow step executes.
   * @type {Function | null}
   * @default null
   */
  export let onExecute = null;

  /**
   * Callback function invoked when the workflow step is rolled back.
   * @type {Function | null}
   * @default null
   */
  export let onRollback = null;

  /**
   * Step configuration object containing step data and metadata.
   * @type {Object}
   * @default { data: {} }
   */
  export let step = { data: {} };

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  let originalItems = [];

  const getBizcardRoutes = async () => {
    const routes = await BizCardsRoutingService.getAll();
    originalItems = JSON.parse(JSON.stringify(routes));
    return routes;
  };

  const saveBizcardRouteOrder = async (items) => {
    const ids = items.map(({ _id }) => _id);

    await BizCardsRoutingService.saveRouteOrder(ids);

    step.data = {
      items,
      rollbackData: {
        originalItems
      }
    };
  };

  const rollbackPriorityChange = async () => {
    if (step.data?.rollbackData?.originalItems) {
      try {
        const ids = step.data.rollbackData.originalItems.map(({ _id }) => _id);
        const response = await BizCardsRoutingService.saveRouteOrder(ids);

        if (response?.status >= 400) {
          notify($format('label.FAILURE.ROLLBACK_ROUTING_PRIORITY'), 'error');
        } else {
          notify($format('label.SUCCESS.ROLLBACK_ROUTING_PRIORITY'));
        }
      } catch (error) {
        console.error('Failed to rollback business card routing priority:', error);
        notify($format('label.FAILURE.ROLLBACK_ROUTING_PRIORITY'), 'error');
      }
    }
  };
</script>

<PriorityChangeForm
  bind:nextDisabled
  bind:step
  {onExecute}
  {onRollback}
  getFunction={getBizcardRoutes}
  saveFunction={saveBizcardRouteOrder}
  rollbackFunction={rollbackPriorityChange}
  itemLabel={$format('label.BUSINESS_CARD_ROUTE')}
>
  <div slot="item" let:item>
    <div class="route-content">
      <div class="route-section">
        <b>{$format('label.BUSINESS_CARD')}</b>
        <div class="blockname">{item.rulename || item.blockname || $format('label.UNNAMED_BUSINESS_CARD')}</div>
      </div>

      {#if item.groups?.length || item.sendergroups?.length}
        <div class="route-section">
          <b>{$format('label.GROUPS')}</b>
          <div class="items">
            {#each item.groups || item.sendergroups || [] as group}
              <div class="item">{typeof group === 'string' ? group : group.groupname}</div>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
</PriorityChangeForm>

<style lang="scss">
  .route-content {
    align-items: center;
    display: grid;
    gap: 24px;
    grid-template-columns: 1fr 1fr;
  }

  .route-section {
    > b {
      display: block;
      font-size: var(--font-size-medium);
      margin-bottom: var(--margin-base);
    }

    .blockname {
      color: var(--color-text);
      display: inline-flex;
      font-size: var(--font-size-small);
    }

    .items {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 4px;

      .item {
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-base);
        color: var(--color-gray-700);
        display: inline-flex;
        font-size: var(--font-size-small);
        padding: var(--padding-small) var(--padding-base);
      }
    }
  }
</style>
